<template>
  <div
    v-if="currentRally"
    id="dg-rally-header"
  >
    <router-link
      v-if="currentRally.parent"
      class="breadcrumb"
      :to="'/' + currentRally.parent.id"
    >
      <i class="fa fa-chevron-left" />
      {{ currentRally.parent.title }}
    </router-link>
    <div v-if="isFirstChannel">
      <img
        class="corner-icon"
        :src="rallyType.cornerIcon"
      >
      <img
        class="rally-avatar"
        :src="avatarUrl"
        :style="`background-color: ${currentRally.avatar?.attributes?.background};`"
        @error="toggleRallyAvatarError"
      >
    </div>
    <div class="content">
      <div class="header">
        <div class="title">
          {{ currentRally.title }}
        </div>
        <div class="actions">
          <router-link
            class="action"
            :to="{ name: 'rallySettings', params: { rallyId: currentRally.id }}"
          >
            <i class="fal fa-cog" />
          </router-link>
          <MultiButton
            v-if="currentRally.editable"
            label="Add Members"
          >
            <p @click="openInvitationModal('single')">
              One At A Time
              <i class="fa fas fa-user-plus" />
            </p>
            <p @click="openInvitationModal('multiple')">
              Email List
              <i class="fa fas fa-envelope" />
            </p>
            <p @click="openInvitationModal('link')">
              Shareable Link
              <i class="fa fas fa-link" />
            </p>
          </MultiButton>
        </div>
      </div>
      <div class="since">
        <img :src="rallyType.inlineIcon">
        {{ createdAtString }}
      </div>
      <div class="details">
        <span
          v-if="currentRally.parent"
          class="stat"
        >
          <b>
            {{ currentRally.parent.title }}
          </b>
        </span>
        <span
          v-if="currentRally['child-count'] > 0"
          class="stat"
        >
          <i class="fa-sitemap fal icon" />
          {{ currentRally["child-count"] }} Rallies
        </span>
        <span class="stast">
          <i class="fal fa-users" />
          {{ Number(currentRally["member-count"]).toLocaleString() }} Members
        </span>
        <span class="access">
          {{ accessText }}
        </span>
      </div>
      <div
        v-if="currentRally.description"
        :class="['cry', cryState.class]"
      >
        {{ currentRally.description }}
      </div>
      <div class="footer">
        <span
          v-if="cryIsLong"
          class="expand-collapse-button"
          @click="toggleCry"
        >
          {{ cryState.label }}
          <i :class="['fa', cryState.icon]" />
        </span>
        <span
          v-else
          class="spacer"
        />
        <i
          class="fa-star favorite-button"
          :class="isFavorite ? 'fas active' : 'far disable'"
          role="button"
          :title="isFavorite ? 'Unfavorite' : 'Favorite'"
          @click.prevent="toggleFavoriteState"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions } from 'vuex'
  import { getChannelSlug } from '@/common/utils'
  import { useBreakpoint } from '@/composables/useBreakpoint'
  import MultiButton from './MultiButton'
  import RallyReloadMixin from './RallyReloadMixin'

  const cryStateMap = [
    {
      class: 'expanded',
      icon: 'fa-chevron-up',
      label: 'View Less',
    },
    {
      class: 'collapsed',
      icon: 'fa-chevron-down',
      label: 'View More',
    },
  ]
  const rallyTypeMap = {
    hub: {
      cornerIcon: '/icons/hub_corner.svg',
      inlineIcon: '/icons/hubicon_grey.svg',
      name: 'Hub',
    },
    rally: {
      cornerIcon: '/icons/rally_corner.svg',
      inlineIcon: '/icons/rallyicon_grey.svg',
      name: 'Rally',
    },
  }

  export default {
    components: {
      MultiButton,
    },
    mixins: [RallyReloadMixin],
    emits: ['perform'],
    data: function() {
      return {
        avatarUrl: null,
        breakpoint: useBreakpoint(),
        cryCollapsed: true,
        rallyAvatarError: false,
      };
    },
    computed: {
      accessText() {
        if ( this.currentRally.editable ) {
          return 'Rally Admin Access'
        } else if ( this.currentRally.writable ) {
          return 'Rally Friend Access'
        } else if ( this.currentRally.viewable ) {
          return 'Read Only Access'
        } else {
          return 'Not A Member'
        }
      },
      createdAtString() {
        const createdAt = new Date(this.currentRally['created-at'])
        const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

        return `${this.rallyType.name} Created ${months[createdAt.getMonth()]} ${createdAt.getFullYear()}`
      },
      currentRally() {
        return this.$store.getters['navigation/currentRally']
      },
      cryIsLong() {
        return this.currentRally.description?.length > 150
      },
      cryState() {
        return cryStateMap[Number(this.cryCollapsed)]
      },
      isFavorite() {
        return this.currentRally['is-user-favorite']
      },
      isFirstChannel() {
        const channels = this.$store.getters['navigation/currentChannels']
        const channelSlug = getChannelSlug(this.$route)

        return channelSlug == channels[0].attributes.slug
      },
      rallyType() {
        const type = this.currentRally['is-hub'] ? 'hub' : 'rally'

        return rallyTypeMap[type]
      },
    },
    watch: {
      'breakpoint.currentSize': function() {
        this.setAvatar()
      },
      'currentRally': function() {
        this.setAvatar()
      },
    },
    async mounted() {
      if ( this.currentRally ) {
        this.setAvatar()
      }
    },
    methods: {
      ...mapActions('rallies', ['toggleFavorite']),
      ...mapActions('user', ['setModalProps']),
      clearParentContainers() {
        this.$emit('perform', 'clearParentContainers')
      },
      openInvitationModal(modal) {
        switch(modal) {
          case 'single':
            this.setModalProps({name: 'InvitationFormModal', rallyId: this.currentRally.id, modalVariant: 'single'})
            break
          case 'multiple':
            this.setModalProps({name: 'InvitationFormModal', rallyId: this.currentRally.id, modalVariant: 'multiple'})
            break
          case 'link':
            this.setModalProps({name: 'InvitationLinkModal', rallyId: this.currentRally.id})
            break
        }
      },
      perform(method, params) {
        params ? this[method](params) : this[method]();
      },
      setAvatar() {
        switch ( this.breakpoint.currentSize ) {
          case 'mobile':
            this.avatarUrl = this.currentRally.avatar.attributes['small-url']
            break
          case 'table':
            this.avatarUrl = this.currentRally.avatar.attributes['medium-url']
            break
          default:
            this.avatarUrl = this.currentRally.avatar.attributes['large-url']
        }
      },
      toggleCry() {
        this.cryCollapsed = !this.cryCollapsed
      },
      toggleFavoriteState() {
        const action = this.isFavorite ? 'unfavorite' : 'favorite'
        // Instantly reflect the change without waiting for response from server.
        this.currentRally['is-user-favorite'] = !this.isFavorite
        this.toggleFavorite({action: action, rallyId: this.currentRally.id})
      },
      toggleRallyAvatarError() {
        this.avatarUrl = '/images/dg_rally_avatar.png'
      },
    }
  }
</script>
